import React, { useCallback, useEffect, useState } from "react";
import { FaEnvelope, FaExternalLinkAlt, FaLock, FaPaperPlane, FaStar } from "react-icons/fa";
import axiosInstance from "../api/axiosInstance";
import { TestResult } from "./VaultPanel";
import { AuditTable, apiError, useCompactView, useVaultAudit } from "./VaultUi";
import "./VaultPanel.css";

/**
 * Settings > Configs, "Email sending": choose which Email account stored in the Vault the company
 * sends from, and test it. Accounts are added and edited in Settings > Vault (`onManage` switches to
 * it); values are never shown here. A test result opens under its row; `showLog` (App Configs header)
 * shows the activity of all email accounts. Table on desktop, cards on tablet / mobile.
 */
function EmailSenderPanel({ basePath, title, onManage, showLog = false }) {
  const compact = useCompactView();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [tests, setTests] = useState({});
  const [testTo, setTestTo] = useState({});
  const [auditKey, setAuditKey] = useState(0);
  const audit = useVaultAudit(basePath, auditKey, showLog);

  const load = useCallback(async () => {
    const res = await axiosInstance.get(basePath);
    setItems(res.data?.data?.items || []);
  }, [basePath]);

  useEffect(() => {
    setLoading(true);
    load()
      .catch((err) => setError(apiError(err, "Could not load email accounts.")))
      .finally(() => setLoading(false));
  }, [load]);

  const sender = items.find((i) => i.Is_Default) || null;

  const choose = async (item) => {
    if (item.Is_Default) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await axiosInstance.post(`${basePath}/${item.Vault_Id}/default`);
      await load();
      setAuditKey((k) => k + 1);
      setNotice(`Emails now go out from ${item.Name}.`);
    } catch (err) {
      setError(apiError(err, "Could not change the sending account."));
    } finally {
      setBusy(false);
    }
  };

  const test = async (item) => {
    setTests((t) => ({ ...t, [item.Vault_Id]: { loading: true } }));
    try {
      const res = await axiosInstance.post(
        `${basePath}/${item.Vault_Id}/test`,
        { send_to: (testTo[item.Vault_Id] || "").trim() || undefined },
        { timeout: 60000 }
      );
      setTests((t) => ({ ...t, [item.Vault_Id]: res.data?.data }));
    } catch (err) {
      setTests((t) => ({ ...t, [item.Vault_Id]: { smtp: { ok: false, message: apiError(err, "Test failed") } } }));
    }
    setAuditKey((k) => k + 1);
  };

  // Test results show under the row; the status only says whether a sender is chosen.
  const status = loading ? null : sender ? { tone: "ok", label: "Active" } : { tone: "warn", label: "Not set up" };

  const defaultPill = (item) =>
    item.Is_Default ? (
      <span className="config-default">
        <span className="config-default-pill is-default" aria-label={`${item.Name} is the default sender`}>
          <FaStar aria-hidden="true" /> Default
        </span>
        {status ? <span className={`config-status config-status--${status.tone}`}>{status.label}</span> : null}
      </span>
    ) : (
      <button
        type="button"
        className="config-default-pill"
        onClick={() => choose(item)}
        disabled={busy}
        title={`Send company emails from ${item.Name}`}
      >
        Set default
      </button>
    );

  const vaultLink = (item) =>
    onManage ? (
      <button
        type="button"
        className="config-vault-link"
        title="Open this account in the Vault"
        onClick={() => onManage(item.Vault_Id)}
      >
        {item.Name} <FaExternalLinkAlt aria-hidden="true" />
      </button>
    ) : (
      <strong>{item.Name}</strong>
    );

  const plain = (value) => (value ? <span className="config-cell-text">{value}</span> : <span className="vault-cell-sub">—</span>);

  const testInput = (item) => (
    <input
      id={`config-test-to-${item.Vault_Id}`}
      type="email"
      className="vault-input"
      aria-label={`Test send address for ${item.Name}`}
      placeholder="Send a test to (optional)"
      value={testTo[item.Vault_Id] || ""}
      onChange={(e) => setTestTo((t) => ({ ...t, [item.Vault_Id]: e.target.value }))}
    />
  );

  const sendButton = (item) => {
    const testing = !!tests[item.Vault_Id]?.loading;
    const hasAddress = !!(testTo[item.Vault_Id] || "").trim();
    return (
      <button
        type="button"
        className="vault-btn vault-btn--sm config-send-btn"
        onClick={() => test(item)}
        disabled={testing}
        title={hasAddress ? "Check the login and send a test email" : "Check the SMTP / IMAP login"}
      >
        <FaPaperPlane aria-hidden="true" /> {testing ? "Sending…" : hasAddress ? "Send" : "Test"}
      </button>
    );
  };

  const testSend = (item) => (
    <div className="config-test-row">
      {testInput(item)}
      {sendButton(item)}
    </div>
  );

  /** Inner row under an account: its load error and / or last test result (null when there is neither). */
  const detail = (item) => {
    const result = tests[item.Vault_Id] && !tests[item.Vault_Id].loading ? tests[item.Vault_Id] : null;
    if (!item.Error && !result) return null;
    return (
      <div className="vault-detail">
        {item.Error ? <div className="vault-alert vault-alert--error">{item.Error}</div> : null}
        {result ? (
          <TestResult result={result} onClose={() => setTests((t) => ({ ...t, [item.Vault_Id]: null }))} />
        ) : null}
      </div>
    );
  };

  const purposeCell = () => (
    <span className="config-purpose__name">
      <FaEnvelope aria-hidden="true" /> {title}
    </span>
  );

  return (
    <div className="vault">
      {showLog ? (
        <div className="vault-log">
          <AuditTable entries={audit.entries} error={audit.error} />
        </div>
      ) : null}
      {error ? <div className="vault-alert vault-alert--error">{error}</div> : null}
      {notice ? <div className="vault-alert vault-alert--ok">{notice}</div> : null}

      {loading ? <div className="vault-empty">Loading…</div> : null}

      {!loading && !items.length ? (
        <div className="config-empty">
          <p>No email accounts in the Vault yet. Add one with type <strong>Email account (SMTP / IMAP)</strong>, then choose it here.</p>
          {onManage ? (
            <button type="button" className="vault-btn vault-btn--primary" onClick={() => onManage()}>
              <FaExternalLinkAlt /> Add in Vault
            </button>
          ) : (
            <p>Ask your company owner to add one in Settings &gt; Vault.</p>
          )}
        </div>
      ) : null}

      {items.length && !compact ? (
        <div className="vault-table__wrap">
          <table className="vault-table" aria-label="Email accounts">
            <thead>
              <tr>
                <th>Purpose</th>
                <th className="vault-table__narrow">Default</th>
                <th>Vault account</th>
                <th>From name</th>
                <th>From email</th>
                <th>Test Send</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => {
                const inner = detail(item);
                const rowClass = [index % 2 ? "is-alt" : "", inner ? "is-open" : ""].filter(Boolean).join(" ");
                return (
                  <React.Fragment key={item.Vault_Id}>
                    <tr className={rowClass}>
                      <td>{purposeCell(item)}</td>
                      <td>{defaultPill(item)}</td>
                      <td>
                        <span className="vault-cell-name">
                          <FaEnvelope aria-hidden="true" className="vault-cell-name__icon" />
                          {vaultLink(item)}
                        </span>
                      </td>
                      <td>{plain(item.From_Name)}</td>
                      <td>{plain(item.From_Email)}</td>
                      <td>{testSend(item)}</td>
                    </tr>
                    {inner ? (
                      <tr className={`vault-table__detail${index % 2 ? " is-alt" : ""}`}>
                        <td colSpan={6}>{inner}</td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {items.length && compact ? (
        <div className="vault-list">
          {items.map((item) => {
            return (
              <article key={item.Vault_Id} className="vault-item">
                <div className="vault-item__head">
                  <span className="vault-item__icon">
                    <FaEnvelope aria-hidden="true" />
                    <span className="vault-item__lock" title="Login details stay in the Vault">
                      <FaLock aria-label="Login details stay in the Vault" />
                    </span>
                  </span>
                  <div className="vault-item__main">
                    <span className="vault-item__title">{vaultLink(item)}</span>
                    <span className="vault-item__badges">{defaultPill(item)}</span>
                  </div>
                </div>
                <dl className="config-fields">
                  <div>
                    <dt>Purpose</dt>
                    <dd>{purposeCell(item)}</dd>
                  </div>
                  <div>
                    <dt>From name</dt>
                    <dd>{plain(item.From_Name)}</dd>
                  </div>
                  <div>
                    <dt>From email</dt>
                    <dd>{plain(item.From_Email)}</dd>
                  </div>
                </dl>
                <div className="config-test-field">
                  <label htmlFor={`config-test-to-${item.Vault_Id}`}>Test Send</label>
                  {testSend(item)}
                </div>
                {detail(item)}
              </article>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default EmailSenderPanel;
